import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import type { ChangeEvent } from "react";
import { toast } from "react-toastify";
import type { User } from "../types/User";
import { useUser } from "../context/UserContext";
import { useUserApi } from "../api/UserApi";
import { handleApiError } from "../utils/handleApiError";
import { useFontenehusActions } from "../hooks/useFontenehusActions";
import { useDeleteUserConfirm } from "../context/ConfirmContext ";
import ChangePasswordModal from "../components/UserSettings/ChangePasswordModal";
const UserSettingsPage: React.FC = () => {
  const { user, setUser } = useUser();
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState<Partial<User>>({});
  const { updateUser, anonymizeUser } = useUserApi();
  const { selectFontenehus } = useFontenehusActions();
  const deleteConfirm = useDeleteUserConfirm();
  const [showChangePassword, setShowChangePassword] = useState(false);
  const navigate = useNavigate();
  // Simulated fetch
  useEffect(() => {
    (() => {
      setFormData(user || {});
    })();
  }, []);

  const handleDeleteUser = async () => {
    try {
      const confirmed = await deleteConfirm({
        title: "Slett/Anonymiser Bruker",
        message:
          "Denne handlingen kan ikke angres.\nSkriv SLETT for å bekrefte.",
        confirmText: "Slett",
        cancelText: "Avbryt",
      });

      if (!confirmed) return;

      await anonymizeUser(user?.id); // call your API
    } catch (err: any) {
      const parsed = JSON.parse(err.request.response);
      if (parsed.success === true) {
        toast.success("Bruker anonymisert");
        navigate("/"); // redirect to home or login page
      } else {
        toast.info(parsed.message);
      }
    } finally {
    }
  };

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    try {
      const hasChanges =
        formData.displayName !== user?.displayName ||
        formData.anonymousSuggestions !== user?.anonymousSuggestions ||
        formData.fontenehusCode !== user?.fontenehusCode;

      if (!hasChanges) {
        toast.info("Ingen endringer å lagre.");
        setEditMode(false);
        return;
      }
      const response = await updateUser(
        {
          displayName: formData.displayName,
          anonymousSuggestions: formData.anonymousSuggestions,
          fontenehusCode: formData.fontenehusCode,
        },
        user?.id!,
      );
      setUser(response.data.data as User);
      setFormData(response.data.data || {});
      selectFontenehus(response.data.data.fontenehusId);
      setEditMode(false);
      toast.success(response.data.message);
      setEditMode(false);
    } catch (err) {
      handleApiError(err, "Kunne ikke oppdatere profilen.");
      return;
    } finally {
    }
  };

  const handleCancel = () => {
    setFormData(user || {});
    setEditMode(false);
  };

  return (
    <div className="min-h-screen bg-orange-50 pt-24 pb-16 px-4 sm:px-8">
      <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-lg p-8">
        <h1 className="text-3xl font-bold text-orange-600 mb-6">
          Brukerinnstillinger
        </h1>

        {/* === Info Fields === */}
        <div className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-600">
              Brukernavn
            </label>
            <input
              type="text"
              name="username"
              value={formData.username || ""}
              onChange={handleChange}
              disabled
              className="w-full mt-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-400 disabled:bg-gray-100 cursor-not-allowed"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-600">
              Visnings Navn
            </label>
            <input
              type="text"
              name="displayName"
              value={formData.displayName || ""}
              onChange={handleChange}
              disabled={!editMode}
              className="w-full mt-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-400 disabled:bg-gray-100"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-600">
              Fontenehus
            </label>
            <input
              type="text"
              name="fontenehusName"
              value={formData.fontenehusName || ""}
              onChange={handleChange}
              disabled
              className="w-full mt-1 px-4 py-2 border rounded-lg bg-gray-100 cursor-not-allowed"
            />
            <label className="block text-sm font-medium text-gray-600">
              Fontenehus Kode
            </label>
            <input
              type="text"
              name="fontenehusCode"
              value={formData.fontenehusCode || ""}
              onChange={handleChange}
              disabled={!editMode}
              className="w-full mt-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-400 disabled:bg-gray-100"
            />
          </div>
        </div>

        {/* ✅ Anonymity toggle */}
        <div className="flex items-center justify-between mt-4">
          <label
            htmlFor="anonymous"
            className="text-sm font-medium text-gray-600"
          >
            Jeg vil være anonym når jeg gir forslag
          </label>
          <input
            type="checkbox"
            id="anonymousSuggestions"
            name="anonymousSuggestions"
            checked={!!formData.anonymousSuggestions}
            disabled={!editMode}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                anonymousSuggestions: e.target.checked,
              }))
            }
            className="w-5 h-5 accent-orange-500 cursor-pointer disabled:opacity-50"
          />
        </div>
        <div>
          <button
            className="px-5 py-2 bg-orange-500 hover:bg-orange-600 mt-4 text-white font-semibold rounded-lg transition"
            onClick={() => setShowChangePassword(true)}
          >
            Bytt passord
          </button>

          <ChangePasswordModal
        isOpen={showChangePassword}
        onClose={() => setShowChangePassword(false)}
      />
        </div>

        <div className="mt-8 flex justify-between items-center">
          <div className="mt-8 p-6 border border-red-300 bg-red-50 rounded-xl">
            <h3 className="text-lg font-semibold text-red-700 mb-2">
              Fareområde
            </h3>
            <p className="text-sm text-red-600 mb-4">
              Her kan du anonymisere brukeren permanent. Dette kan ikke angres.
            </p>

            <button
              onClick={handleDeleteUser}
              className="w-full px-4 py-2 text-white font-semibold bg-red-600 hover:bg-red-700 rounded-lg shadow transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {"Slett Bruker"}
            </button>
          </div>

          {/* Edit/Save group on right */}
          <div className="flex gap-3">
            {!editMode ? (
              <button
                onClick={() => setEditMode(true)}
                className="px-5 py-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-lg transition"
              >
                Rediger
              </button>
            ) : (
              <>
                <button
                  onClick={handleCancel}
                  className="px-5 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold rounded-lg transition"
                >
                  Avbryt
                </button>
                <button
                  onClick={handleSave}
                  className="px-5 py-2 bg-green-500 hover:bg-green-600 text-white font-semibold rounded-lg transition"
                >
                  Lagre
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserSettingsPage;
